import { create } from 'zustand';
import type { ToastMessage } from '@/types';

interface ToastState {
  toasts: ToastMessage[];
  show: (
    message: string,
    options?: {
      type?: ToastMessage['type'];
      duration?: number;
      action?: ToastMessage['action'];
    },
  ) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  show: (message, options = {}) => {
    const { type = 'info', duration = 3500, action } = options;
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const toast: ToastMessage = { id, type, message, duration, action };
    set((state) => ({ toasts: [...state.toasts, toast] }));
    if (duration > 0) {
      setTimeout(() => {
        set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
      }, duration);
    }
    return id;
  },
  dismiss: (id) => set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
  clear: () => set({ toasts: [] }),
}));

// Convenience export — call directly without hook
export const toast = {
  success: (msg: string, duration?: number) =>
    useToastStore.getState().show(msg, { type: 'success', duration }),
  error: (msg: string, duration?: number) =>
    useToastStore.getState().show(msg, { type: 'error', duration }),
  warning: (msg: string, duration?: number) =>
    useToastStore.getState().show(msg, { type: 'warning', duration }),
  info: (msg: string, duration?: number) =>
    useToastStore.getState().show(msg, { type: 'info', duration }),
  /**
   * Show a toast with an action button (e.g., Undo).
   * The action button dismisses the toast when clicked.
   */
  withAction: (
    msg: string,
    actionLabel: string,
    onAction: () => void,
    options?: { type?: ToastMessage['type']; duration?: number },
  ) =>
    useToastStore.getState().show(msg, {
      type: options?.type ?? 'info',
      duration: options?.duration ?? 4000,
      action: { label: actionLabel, onClick: onAction },
    }),
  /**
   * Undo toast: "<message> · Undo". The action reverts the change; it stays for
   * 10s so there's a real window to catch an accidental delete/edit. Used by the
   * store's owner-facing mutations, which snapshot the prior state and pass a
   * restore function here.
   */
  undo: (msg: string, onUndo: () => void, duration = 10000) =>
    useToastStore.getState().show(msg, {
      type: 'info',
      duration,
      action: { label: 'Undo', onClick: onUndo },
    }),
};
